import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Search,
  X,
  Filter,
  Calendar,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Package,
  AlertCircle,
  Eye,
  Send,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Calendar as CalendarIcon,
  Edit,
  LayoutGrid,
  List,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useAppSelector } from "../../store/hooks";
// AlterCPA removed for Greek-CC
// import {
//   getAccountById,
// } from "../../store/slices/altercpaSlice";
import {
  Order,
  CountryPagination,
  CountrySettings,
  User as UserType,
} from "./types";
import {
  getCountryFlag,
  getShippingCompanyForCountry,
  formatPhoneNumber,
  getAlterCPAStatus,
  getPostOfficeStatus,
} from "./utils";
import PostOfficeSelector from "./PostOfficeSelector";
import EditOrderModal from "./EditOrderModal";
// BEX modal removed for Greek-CC (Greece uses ACS only)
// import BexEditOrderModal from "./BexEditOrderModal";
import PickupDateModal from "./PickupDateModal";
import ViewAllSelectionModal from "./ViewAllSelectionModal";
import { API_BASE_URL } from "../../utils/apiConfig";

interface Props {
  country: string;
  settings: any;
  refreshKey: number;
}


// Helper function to parse MongoDB date objects or regular dates
const parseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  // Handle MongoDB date objects like {"$date": "2025-10-16T10:12:15.370Z"}
  if (typeof dateValue === 'object' && dateValue !== null) {
    if (dateValue.$date) {
      return new Date(dateValue.$date);
    }
    // Handle Date objects directly
    if (dateValue instanceof Date) {
      return dateValue;
    }
  }
  
  // Handle regular date strings
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  
  // Handle Unix timestamps
  if (typeof dateValue === 'number') {
    // Handle Unix timestamps - if it's less than 13 digits, it's in seconds and needs to be converted to milliseconds
    // If it's 13 digits or more, it's already in milliseconds
    return new Date(dateValue < 10000000000 ? dateValue * 1000 : dateValue);
  }
  
  return null;
};

// Helper function to determine an order's stage
const getOrderStage = (order: Order): "without-post-office" | "with-post-office" | "shipped" => {
  if (order.trackingId) return "shipped";
  if (order.selectedPostOffice) return "with-post-office";
  return "without-post-office";
};

// Helper function to get the best available timestamp for display and sorting
const getBestTimestamp = (order: Order): number | null => {
  // Priority order: packingTimestamp > altercpaPackingStateTime > lastUpdated > done
  
  // Check packingTimestamp first (can be Unix timestamp or MongoDB date object)
  if (order.packingTimestamp) {
    const date = parseDate(order.packingTimestamp);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // Check altercpaPackingStateTime (ISO date string or MongoDB date object)
  if (order.altercpaPackingStateTime) {
    const date = parseDate(order.altercpaPackingStateTime);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // Check lastUpdated (ISO date string or MongoDB date object)
  if (order.lastUpdated) {
    const date = parseDate(order.lastUpdated);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  // For manual orders, check done timestamp as fallback
  if (order.done) {
    const date = parseDate(order.done);
    if (date && !isNaN(date.getTime())) {
      return date.getTime();
    }
  }
  
  return null;
};

export interface CountrySectionProps {
  country: string;
  currentUser?: UserType;
  isExpanded: boolean;
  onToggleExpansion: (country: string) => void;
  settings: CountrySettings[string];
  pagination: CountryPagination[string];
  selectedOrders: Set<number>;
  selectedOrdersMeta: Record<number, Order>;
  getSelectedOrdersForCountry: (country: string) => Order[];
  sendingToShipping: Set<number>;
  isBulkSelectionMode: boolean;
  onToggleOrderSelection: (orderId: number, order?: Order) => void;
  onToggleCountryOrdersSelection: (country: string, selectAll: boolean) => void;
  currentSelectionStage: "without-post-office" | "with-post-office" | "shipped" | "mixed" | null;
  onUpdateStage: (stage: "without-post-office" | "with-post-office" | "shipped" | "mixed" | null) => void;
  onRefreshSelectedOrdersMeta: (country: string, freshOrders?: Order[]) => void;
  onStartBulkOperation?: (country: string) => void;
  onStopBulkOperation?: (country: string) => void;
  pendingNextStageSelections: Set<number>;
  getPendingNextStageOrders: () => Order[];
  onProgressToNextStage: () => void;
  onClearPendingSelections: () => void;
  onSendToBex: (orderId: number, pickupDate: string, notes?: string) => void;
  onSelectPostOffice: (orderId: number, postOffice: string) => Promise<void>;
  onClearPostOffice: (orderId: number) => Promise<void>;
  onSendToShipping: (
    orderId: number,
    postOffice: string,
    pickupDate: string,
    notes?: string
  ) => Promise<void>;
  onPrintLabel: (orderId: number, labelType: "a6" | "a4") => Promise<void>;
  onViewLabel: (orderId: number, labelType: "a6" | "a4") => void;
  onDeleteShipment: (orderId: number) => Promise<void>;
  onEditOrder: (orderId: number, updates: Partial<Order>) => Promise<void>;
  onCountrySearch: (country: string, searchTerm: string) => void;
  onCountrySort: (
    country: string,
    sortBy: string,
    sortOrder: "asc" | "desc" | "none"
  ) => void;
  onCountryFilter: (country: string, statusFilter: string) => void;
  onCountryPageChange: (country: string, page: number) => void;
  onLoadCountryData: (country: string, page: number) => void;
  onUpdateCountrySettings: (
    country: string,
    updates: Partial<CountrySettings[string]>
  ) => void;
  onLoadCountryDataWithFilters: (
    country: string,
    page: number,
    filters: {
      search?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc" | "none";
      statusFilter?: string;
      periodFilter?: string;
      timePeriodBy?: string;
      customStartDate?: string;
      customEndDate?: string;
    }
  ) => void;
  viewMode?: "table" | "cards";
}

const CountrySection: React.FC<CountrySectionProps> = ({
  country,
  currentUser,
  isExpanded,
  onToggleExpansion,
  settings,
  pagination,
  selectedOrders,
  selectedOrdersMeta,
  getSelectedOrdersForCountry,
  sendingToShipping,
  isBulkSelectionMode,
  onToggleOrderSelection,
  onToggleCountryOrdersSelection,
  currentSelectionStage,
  onUpdateStage,
  onRefreshSelectedOrdersMeta,
  onStartBulkOperation,
  onStopBulkOperation,
  pendingNextStageSelections,
  getPendingNextStageOrders,
  onProgressToNextStage,
  onClearPendingSelections,
  onSendToBex,
  onSelectPostOffice,
  onClearPostOffice,
  onSendToShipping,
  onPrintLabel,
  onViewLabel,
  onDeleteShipment,
  onEditOrder,
  onCountrySearch,
  onCountrySort,
  onCountryFilter,
  onCountryPageChange,
  onLoadCountryData,
  onUpdateCountrySettings,
  onLoadCountryDataWithFilters,
  viewMode = "cards",
}) => {
  // AlterCPA removed for Greek-CC
  // const altercpaState = useAppSelector((state) => state.altercpa);
  const shippingCompany = getShippingCompanyForCountry(country);
  const isConnected = shippingCompany?.status === "connected";
  const orders = settings.orders || [];

  // Edit modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    order: Order | null;
  }>({
    isOpen: false,
    order: null,
  });

  // Pickup date modal state
  const [pickupDateModal, setPickupDateModal] = useState<{
    isOpen: boolean;
    orderId: number | null;
    orderIds: number[] | null;
    shippingCompany: string;
    isBulk: boolean;
    orders?: Array<{ id: number; price: number; currency: string }>;
  }>({
    isOpen: false,
    orderId: null,
    orderIds: null,
    shippingCompany: "",
    isBulk: false,
  });

  // View all selection modal state
  const [viewAllSelectionModal, setViewAllSelectionModal] = useState(false);

  // Use stats from backend (includes period filter and all orders, not just current page)
  const stats = settings.stats || {
    totalShipments: 0,
    registered: 0,
    inTransit: 0,
    delivered: 0,
    returned: 0,
  };
  const packagingOrders =
    (settings.stats?.packagingOrders as number | undefined) || 0;

  const isSerbiaSection = country.toLowerCase() === "rs";
  const EXTRA_SHIPPING_THRESHOLD_RSD = 4000;
  const EXTRA_SHIPPING_FEE_RSD = 330;

  const normalizeCurrency = (currency?: string) =>
    (currency || (isSerbiaSection ? "RSD" : "")).toString().trim().toUpperCase();

  const formatPriceValue = (value: number, currency: string) => {
    const formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `${formatter.format(value)} ${currency}`.trim();
  };

  const getOrderPricing = (order: Order) => {
    let quantity = Number(order.count) || 1;
    let unitPrice = 0;
    let subtotal = Number(order.price) || 0; // Total price from order
    
    // Calculate from goods array if available
    if (order.goods && order.goods.length > 0) {
      // Total count = sum of all goods counts
      quantity = order.goods.reduce((sum: number, good: any) => sum + (Number(good.count) || 0), 0);
      // Unit price = price from first good (goods[0].price)
      unitPrice = Number(order.goods[0]?.price) || 0;
      // Subtotal = sum of (goods[i].price * goods[i].count) for all goods
      subtotal = order.goods.reduce((sum: number, good: any) => {
        return sum + ((Number(good.price) || 0) * (Number(good.count) || 0));
      }, 0);
    } else {
      // Fallback: calculate unit price from order.price / order.count
      if (quantity > 0) {
        unitPrice = subtotal / quantity;
      }
    }
    
    const currency = normalizeCurrency(order.currency);
    const isDinCurrency = ["RSD", "DIN", "DINAR", "DINARA"].includes(currency);
    const needsExtraShipping =
      isSerbiaSection && isDinCurrency && subtotal > 0 && subtotal < EXTRA_SHIPPING_THRESHOLD_RSD;
    const extraShippingFee = needsExtraShipping ? EXTRA_SHIPPING_FEE_RSD : 0;
    const total = subtotal + extraShippingFee;

    return {
      quantity,
      unitPrice,
      subtotal,
      total,
      currency: currency || (isSerbiaSection ? "RSD" : ""),
      extraShippingFee,
      needsExtraShipping,
    };
  };

  // Bulk post office selection state
  const [showBulkPostOfficeSelector, setShowBulkPostOfficeSelector] =
    useState(false);
  const [bulkPostOffice, setBulkPostOffice] = useState<string>("");
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Force re-render state
  const [refreshKey, setRefreshKey] = useState(0);

  // Custom date range state
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Applied custom date range (only updates when user clicks "Apply Range")
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  // Delivered stats for ACS (fetched from acsTrackingStatus)
  const [deliveredStats, setDeliveredStats] = useState<{
    Transit: number;
    Delivered: number;
    Returned: number;
    Complicated: number;
  } | null>(null);

  // Calculate filtered stats from displayed orders using the same logic as ACS delivered-stats
  // Use shippingStats from backend instead of calculating locally
  // Backend provides accurate stats based on ACS flags
  const filteredStats = React.useMemo(() => {
    const shippingStats = settings.stats || {};
    
    // Map backend stats to frontend display format
    return {
      Transit: shippingStats.inTransit || 0,
      Delivered: shippingStats.delivered || 0,
      Returned: shippingStats.returned || 0,
      Complicated: 0, // Calculate complicated from displayed orders
    };
  }, [settings.stats]);
  
  // Calculate complicated count from displayed orders
  const complicatedCount = React.useMemo(() => {
    return orders.filter(order => order.acsTrackingStatus?.isComplicated).length;
  }, [orders]);


  // Fetch delivered stats for Greece when expanded or period changes
  React.useEffect(() => {
    if (isExpanded && country.toLowerCase() === "gr") {
      fetchDeliveredStatsForCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, settings.periodFilter, settings.timePeriodBy, country, appliedCustomDateRange.start, appliedCustomDateRange.end]);

  // Force re-render when settings change to ensure UI updates
  React.useEffect(() => {
    console.log(`🔄 [UI REFRESH] ${country} settings changed:`, {
      periodFilter: settings.periodFilter,
      timePeriodBy: settings.timePeriodBy,
      ordersCount: (settings.orders || []).length,
      paginationTotalCount: pagination.totalCount,
      stats: settings.stats
    });
  }, [settings.periodFilter, settings.timePeriodBy, settings.orders, settings.stats, pagination.totalCount, country]);

  // Auto-refresh metadata when orders change after bulk operations
  React.useEffect(() => {
    if (selectedOrders.size > 0 && settings.orders && settings.orders.length > 0) {
      // Check if any selected order has updated data
      const hasStaleData = Array.from(selectedOrders).some(orderId => {
        const currentMeta = selectedOrdersMeta[orderId];
        const freshOrder = settings.orders.find((o: Order) => o.id === orderId);
        
        if (!freshOrder || !currentMeta) return false;
        
        // Check if any critical field changed
        const metaChanged = 
          (currentMeta.selectedPostOffice !== freshOrder.selectedPostOffice) ||
          (currentMeta.trackingId !== freshOrder.trackingId);
          
        if (metaChanged) {
          console.log(`🔄 [AUTO-REFRESH] Order ${orderId} data changed, refreshing metadata`);
        }
        
        return metaChanged;
      });
      
      if (hasStaleData) {
        onRefreshSelectedOrdersMeta(country, settings.orders);
      }
    }
  }, [settings.orders, selectedOrders, selectedOrdersMeta, country, onRefreshSelectedOrdersMeta]);

  const fetchDeliveredStatsForCountry = async () => {
    try {
      // Use the same period calculation as the main data
      const periodToUse = settings.periodFilter || "current_month";

      const params = new URLSearchParams();
      
      // Handle custom date range
      if (periodToUse === "custom") {
        params.append("customStartDate", appliedCustomDateRange.start);
        params.append("customEndDate", appliedCustomDateRange.end);
        params.append("dateRange", "custom");
        console.log(`🔍 [ACS-STATS] Using custom date range for ${country}:`, appliedCustomDateRange.start, "to", appliedCustomDateRange.end);
      } else {
        params.append("dateRange", periodToUse.replace("current_", "this_"));
      }
      
      params.append("country", country.toUpperCase());

      const response = await fetch(
        `${API_BASE_URL}/api/shipping/acs/delivered-stats?${params.toString()}`
      );
      const result = await response.json();

      if (result.success) {
        setDeliveredStats(result.data.summary);
      }
    } catch (error) {
      console.error(`Failed to fetch delivered stats for ${country}:`, error);
    }
  };

  // Memoize selected orders compatibility state to avoid recalculating on every render
  const selectedOrdersCompatibility = React.useMemo(() => {
    // Use cross-page meta snapshot for the current country
    const selectedOrdersList = getSelectedOrdersForCountry(country);

    if (selectedOrdersList.length === 0) {
      return { type: "none", canSelect: true, count: 0, hasPickupList: false };
    }

    const allWithoutPostOffice = selectedOrdersList.every(
      (order) => !order.selectedPostOffice && !order.trackingId
    );
    const allWithPostOffice = selectedOrdersList.every(
      (order) => order.selectedPostOffice && !order.trackingId
    );
    const allShipped = selectedOrdersList.every((order) => order.trackingId);

    // Check if any order has a pickup list issued
    const hasPickupList = selectedOrdersList.some(
      (order) => order.pickupListNumber || order.pickupListIssued
    );

    // If all orders have tracking IDs (shipped), they can be bulk deleted/previewed
    // BUT only if they don't have pickup lists issued yet
    if (allShipped)
      return {
        type: "shipped",
        canSelect: true,
        count: selectedOrdersList.length,
        hasPickupList: hasPickupList,
      };
    if (allWithoutPostOffice)
      return {
        type: "without-post-office",
        canSelect: true,
        count: selectedOrdersList.length,
        hasPickupList: false,
      };
    if (allWithPostOffice)
      return {
        type: "with-post-office",
        canSelect: true,
        count: selectedOrdersList.length,
        hasPickupList: false,
      };
    return {
      type: "mixed",
      canSelect: false,
      count: selectedOrdersList.length,
      hasPickupList: false,
    };
  }, [getSelectedOrdersForCountry, country, selectedOrders]);

  // Wrapper to open pickup date modal before shipping
  const handleSendToShipping = async (orderId: number, postOffice: string) => {
    // Find the order to get its price and currency
    const order = orders.find(o => o.id === orderId);
    
    setPickupDateModal({
      isOpen: true,
      orderId,
      orderIds: null,
      shippingCompany: postOffice,
      isBulk: false,
      orders: order ? [{
        id: order.id,
        price: order.price || 0,
        currency: order.currency || 'EUR'
      }] : [],
    });
    return Promise.resolve();
  };

  // Handle bulk shipping (send to shipping)
  const handleBulkSendToShipping = () => {
    // Get selected orders for this country across all pages
    const selectedOrdersList = getSelectedOrdersForCountry(country);
    const orderIds = selectedOrdersList.map((order) => order.id);

    if (orderIds.length === 0) return;

    // Determine the shipping company from the first order's post office
    const firstOrder = selectedOrdersList[0];
    const shippingCompany = firstOrder.selectedPostOffice?.toLowerCase() || "";

    console.log(
      `🚚 [BULK] Opening pickup date modal for ${orderIds.length} orders to ${shippingCompany}`
    );

    // Open pickup date modal for bulk shipping
    setPickupDateModal({
      isOpen: true,
      orderId: null,
      orderIds: orderIds,
      shippingCompany: shippingCompany,
      isBulk: true,
      orders: selectedOrdersList.map(order => ({
        id: order.id,
        price: order.price || 0,
        currency: order.currency || 'EUR'
      }))
    });
  };

  // Handle bulk delete (for shipped orders)
  const handleBulkDelete = async () => {
    const selectedOrdersList = orders.filter((order) =>
      selectedOrders.has(order.id)
    );

    if (selectedOrdersList.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedOrdersList.length} shipment${
        selectedOrdersList.length !== 1 ? "s" : ""
      }?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setIsBulkProcessing(true);
    try {
      console.log(
        `🗑️ [BULK DELETE] Deleting ${selectedOrdersList.length} shipments`
      );

      let successCount = 0;
      let failCount = 0;

      // Process each order sequentially
      for (const order of selectedOrdersList) {
        try {
          await onDeleteShipment(order.id);
          successCount++;
          console.log(
            `✅ [BULK DELETE] Deleted shipment for order ${order.id}`
          );
          // Small delay between requests
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          failCount++;
          console.error(
            `❌ [BULK DELETE] Failed to delete shipment for order ${order.id}:`,
            error
          );
        }
      }

      console.log(
        `✅ [BULK DELETE] Completed: ${successCount} successful, ${failCount} failed`
      );

      // Reload data once after all deletions but keep current page
      await onLoadCountryDataWithFilters(country, pagination.currentPage, {
        search: settings.search,
        sortBy: settings.sortBy,
        sortOrder: settings.sortOrder,
        statusFilter: settings.statusFilter,
        periodFilter: settings.periodFilter,
        timePeriodBy: settings.timePeriodBy,
      });

      // Clear selection after deletion
      onToggleCountryOrdersSelection(country, false);

      if (failCount > 0) {
        alert(
          `Deleted ${successCount} shipment${
            successCount !== 1 ? "s" : ""
          }. ${failCount} failed - check console for details.`
        );
      }
    } catch (error) {
      console.error("❌ [BULK DELETE] Bulk delete failed:", error);
      alert("Error deleting shipments. Please try again.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Handle bulk preview (supports unlimited orders, batches of 9)
  const handleBulkPreview = async () => {
    // Get all selected orders for this country across all pages
    const selectedOrdersList = getSelectedOrdersForCountry(country);

    if (selectedOrdersList.length === 0) {
      alert("Please select at least 1 order to preview.");
      return;
    }

    // Get tracking IDs from selected orders
    const trackingIds = selectedOrdersList
      .map((order) => order.trackingId)
      .filter((id) => id);

    if (trackingIds.length !== selectedOrdersList.length) {
      alert("All selected orders must have tracking numbers.");
      return;
    }

    // Check if any order already has a pickup list issued
    const ordersWithPickupList = selectedOrdersList.filter(
      (order) => order.pickupListNumber || order.pickupListIssued
    );

    if (ordersWithPickupList.length > 0) {
      const orderIds = ordersWithPickupList.map((o) => `#${o.id}`).join(", ");
      alert(
        `Cannot preview labels - pickup list already issued for:\n${orderIds}\n\nOrders with pickup lists cannot be previewed individually.`
      );
      return;
    }

    // Get shipping company from first order
    const shippingCompany = selectedOrdersList[0].shippingCompany || "";

    console.log(
      `📄 [BULK PREVIEW] Generating preview for ${trackingIds.length} orders in batches of 9`
    );

    setIsBulkProcessing(true);
    try {
      // Process in batches of 9 (ACS API limit)
      const batchSize = 9;
      let pdfCount = 0;

      for (let i = 0; i < trackingIds.length; i += batchSize) {
        const batch = trackingIds.slice(i, i + batchSize);
        
        // Build query string with tracking IDs for this batch (max 9)
        const trackingParams = batch
          .map(
            (id, index) =>
              `trackingId${index + 1}=${encodeURIComponent(id || "")}`
          )
          .join("&");
        const apiUrl = `${API_BASE_URL}/api/shipping/${shippingCompany}/print-bulk?${trackingParams}&printType=2&startPosition=1`;

        console.log(`📄 [BULK PREVIEW] Batch ${i / batchSize + 1}: ${batch.length} orders`);

        const response = await fetch(apiUrl, {
          method: "GET",
        });

        const result = await response.json();

        if (result.success) {
          // Extract PDF base64 from response
          const pdfBase64 =
            result.data?.pdfBase64 || result.data?.base64 || result.pdfBase64;

          if (pdfBase64) {
            console.log(`✅ [BULK PREVIEW] Batch ${i / batchSize + 1} PDF generated`);

            // Download the PDF
            const link = document.createElement("a");
            link.href = `data:application/pdf;base64,${pdfBase64}`;
            link.download = `labels_batch_${i / batchSize + 1}_${batch[0]}.pdf`;
            link.target = '_blank'; // Open in new tab
            link.click();
            
            pdfCount++;
          } else {
            console.error("❌ [BULK PREVIEW] PDF data not found for batch", i / batchSize + 1);
          }
        } else {
          console.error("❌ [BULK PREVIEW] Batch failed:", result.error);
        }
      }

      console.log(`✅ [BULK PREVIEW] Generated ${pdfCount} PDF files`);
      
      if (pdfCount === 0) {
        alert("Failed to generate any PDF files");
      }
    } catch (error) {
      console.error("❌ [BULK PREVIEW] Error:", error);
      alert("Error generating preview. Please try again.");
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Handle bulk clear post office
  const handleBulkClearPostOffice = React.useCallback(async () => {
    const selectedOrdersList = getSelectedOrdersForCountry(country);
    const orderIds = selectedOrdersList.map((order) => order.id);

    if (orderIds.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to clear the post office for ${orderIds.length} order${
        orderIds.length !== 1 ? "s" : ""
      }?`
    );

    if (!confirmed) return;

    setIsBulkProcessing(true);
    
    // Start tracking bulk operation to suppress conflict modal
    onStartBulkOperation?.(country);
    
    // IMMEDIATELY update metadata to reflect the new stage (before calling onUpdateStage)
    // This prevents the "mixed" message from showing
    // Use selectedOrdersMeta to ensure we have complete order data (including address, etc.)
    const updatedMeta = { ...selectedOrdersMeta };
    orderIds.forEach(orderId => {
      if (updatedMeta[orderId]) {
        updatedMeta[orderId] = {
          ...updatedMeta[orderId], // Preserve ALL fields (address, customer data, etc.)
          selectedPostOffice: undefined,
          postOfficeSelectedAt: undefined,
        };
      }
    });
    onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
    console.log('🔄 [BULK] Updated metadata immediately to "without-post-office" with ALL fields preserved');
    
    // Update stage
    onUpdateStage("without-post-office");
    console.log('🔄 [BULK] Stage updated to "without-post-office" immediately');
    
    try {
      console.log(
        `🗑️ [BULK] Clearing post office for ${orderIds.length} orders:`,
        orderIds
      );

      // Clear post office for each order
      for (const orderId of orderIds) {
        try {
          await onClearPostOffice(orderId);
          console.log(
            `✅ [BULK] Cleared post office for order ${orderId}`
          );
          // Small delay between requests
          await new Promise((resolve) => setTimeout(resolve, 200));
        } catch (error) {
          console.error(
            `❌ [BULK] Failed to clear post office for order ${orderId}:`,
            error
          );
        }
      }

      console.log(`✅ [BULK] Completed clearing post office for ${orderIds.length} orders`);

      // Get all affected countries from selected orders
      const affectedCountries = new Set<string>();
      selectedOrdersList.forEach(order => {
        affectedCountries.add(order.country?.toLowerCase() || '');
      });

      // Reload data for all affected countries but keep current page
      console.log(`🔄 [BULK] Reloading data for ${affectedCountries.size} countries on page ${pagination.currentPage}`);
      for (const affectedCountry of Array.from(affectedCountries)) {
        // Check if this is the current country, preserve page number
        const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
        await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
          search: settings.search,
          sortBy: settings.sortBy,
          sortOrder: settings.sortOrder,
          statusFilter: settings.statusFilter,
          periodFilter: settings.periodFilter,
          timePeriodBy: settings.timePeriodBy,
        });
      }

      console.log('✅ [BULK] Data reloaded and stage is "without-post-office"');
    } catch (error) {
      console.error("❌ [BULK] Bulk clear post office failed:", error);
      alert("Error clearing post office. Please try again.");
    } finally {
      // Stop tracking bulk operation
      onStopBulkOperation?.(country);
      setIsBulkProcessing(false);
    }
  }, [getSelectedOrdersForCountry, country, onClearPostOffice, onLoadCountryDataWithFilters, onUpdateStage, onStartBulkOperation, onStopBulkOperation, onRefreshSelectedOrdersMeta, settings, pagination, selectedOrdersMeta]);

  // Handle bulk post office selection
  const handleBulkPostOfficeSelect = async () => {
    if (!bulkPostOffice) return;

    setIsBulkProcessing(true);
    
    // Start tracking bulk operation to suppress conflict modal
    onStartBulkOperation?.(country);
    
    // IMMEDIATELY update metadata to reflect the new stage
    // Use selectedOrdersMeta to ensure we have complete order data (including address, etc.)
    const updatedMeta = { ...selectedOrdersMeta };
    const selectedOrderIds = Array.from(selectedOrders);
    selectedOrderIds.forEach(orderId => {
      if (updatedMeta[orderId]) {
        updatedMeta[orderId] = {
          ...updatedMeta[orderId], // Preserve ALL fields (address, customer data, etc.)
          selectedPostOffice: bulkPostOffice,
          postOfficeSelectedAt: new Date().toISOString(),
        };
      }
    });
    onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
    console.log('🔄 [BULK] Updated metadata immediately to "with-post-office" with ALL fields preserved');
    
    // Update stage
    onUpdateStage("with-post-office");
    console.log('🔄 [BULK] Stage updated to "with-post-office" immediately');
    
    try {
      const selectedOrderIds = Array.from(selectedOrders);

      console.log(
        `📮 [BULK] Selecting post office ${bulkPostOffice} for ${selectedOrderIds.length} orders:`,
        selectedOrderIds
      );

      // Call backend API with bulk orderIds
      const response = await fetch(
        `${API_BASE_URL}/api/shipping/select-post-office`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            orderIds: selectedOrderIds,
            postOffice: bulkPostOffice,
          }),
        }
      );

      const result = await response.json();

      if (result.success || result.data?.successful?.length > 0) {
        console.log(
          `✅ [BULK] Successfully selected post office for ${result.data.successful.length} orders`
        );

        if (result.data?.failed?.length > 0) {
          console.warn(
            `⚠️ [BULK] Failed for ${result.data.failed.length} orders:`,
            result.data.failed
          );
          alert(
            `Post office selected for ${result.data.successful.length} orders.\n${result.data.failed.length} failed - check console for details.`
          );
        }

        // Update selectedOrdersMeta with fresh data from API response
        if (result.data?.successful) {
          const updatedMeta = { ...selectedOrdersMeta };
          result.data.successful.forEach((successfulOrder: any) => {
            const existingOrder = updatedMeta[successfulOrder.orderId];
            if (existingOrder) {
              updatedMeta[successfulOrder.orderId] = {
                ...existingOrder,
                selectedPostOffice: successfulOrder.selectedPostOffice,
                postOfficeSelectedAt: successfulOrder.postOfficeSelectedAt,
              };
            }
          });
          onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
          console.log(`✅ [BULK] Updated metadata for ${result.data.successful.length} orders from API response`);
        }

        // Get all affected countries from selected orders
        const selectedOrdersList = getSelectedOrdersForCountry(country);
        const affectedCountries = new Set<string>();
        selectedOrdersList.forEach(order => {
          affectedCountries.add(order.country?.toLowerCase() || '');
        });

        // Reload data for all affected countries but keep current page
        console.log(`🔄 [BULK] Reloading data for ${affectedCountries.size} countries on page ${pagination.currentPage}`);
        for (const affectedCountry of Array.from(affectedCountries)) {
          // Check if this is the current country, preserve page number
          const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
          await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
            search: settings.search,
            sortBy: settings.sortBy,
            sortOrder: settings.sortOrder,
            statusFilter: settings.statusFilter,
            periodFilter: settings.periodFilter,
            timePeriodBy: settings.timePeriodBy,
          });
        }

        // DON'T clear selection - keep them selected
        // Metadata will be auto-refreshed by useEffect when settings.orders updates
        
        console.log('✅ [BULK] Post office selected, stage is already "with-post-office".');
        
        setShowBulkPostOfficeSelector(false);
        setBulkPostOffice("");
      } else {
        console.error("❌ [BULK] All orders failed:", result);
        alert(
          `Failed to select post office: ${result.error || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("❌ [BULK] Bulk post office selection failed:", error);
      alert("Error selecting post office for orders. Please try again.");
    } finally {
      // Stop tracking bulk operation
      onStopBulkOperation?.(country);
      setIsBulkProcessing(false);
    }
  };

  // Local view mode state (defaults to table on desktop, cards on mobile)
  const [localViewMode, setLocalViewMode] = useState<"table" | "cards">(() => {
    // Default based on screen size: table for desktop (>= 640px), cards for mobile
    // If viewMode prop is explicitly provided and different from default, use it
    const screenBasedDefault = window.innerWidth >= 640 ? "table" : "cards";
    // If prop is explicitly set to something other than the default, use it
    // Otherwise use screen-based default
    return viewMode !== "cards" ? viewMode : screenBasedDefault;
  });
  
  // Update local view mode when prop changes (but respect user's manual selection)
  React.useEffect(() => {
    // Only update from prop if it's explicitly set to a different value
    // This allows the prop to override but won't reset user's manual selection
    if (viewMode !== "cards") {
      setLocalViewMode(viewMode);
    }
  }, [viewMode]);

  const [maxWidth, setMaxWidth] = useState<string>("100%");

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      
      // Calculate padding based on breakpoints (matching parent: px-4 sm:px-6 lg:px-8)
      // px-4 = 16px (1rem), sm:px-6 = 24px (1.5rem), lg:px-8 = 32px (2rem)
      let pagePadding = 20; // default mobile padding (px-4)
      if (windowWidth >= 640) { // sm breakpoint
        pagePadding = 28; // sm:px-6
      }
      if (windowWidth >= 1024) { // lg breakpoint
        pagePadding = 36; // lg:px-8
      }
      
      // Sidebar only exists on lg+ screens (1024px+) and is 256px wide (w-64)
      const sideMenuWidth = windowWidth >= 1024 ? 256 : 0;
      
      // Total horizontal padding (left + right)
      const totalPadding = pagePadding * 2;
      
      const newWidth = windowWidth - sideMenuWidth - totalPadding;
      setMaxWidth(`${newWidth}px`);
    };

    handleResize(); // initial
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <div
      key={`${country}-${settings.periodFilter}-${settings.timePeriodBy}-${(settings.orders || []).length}-${refreshKey}`}
      className="bg-white rounded-lg shadow mb-6 overflow-hidden w-full max-w-full"
      style={{ maxWidth }}
    >
      {/* Country Header - Clickable */}
      <div
        className={`px-4 sm:px-6 py-4 border-b border-gray-200 cursor-pointer transition-colors hover:bg-opacity-80 ${
          !isConnected ? "bg-red-50 border-red-200" : "bg-gray-50"
        }`}
        onClick={() => onToggleExpansion(country)}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-shrink-0">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-500" />
              )}
              <span className="text-2xl">{getCountryFlag(country)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {country.toUpperCase()} - {pagination.totalCount} Orders
              </h3>
              {pagination.loading && (
                <div className="flex items-center mt-1">
                  <RefreshCw className="w-3 h-3 animate-spin text-blue-600 mr-1" />
                  <span className="text-xs text-blue-600">Loading...</span>
                </div>
              )}
            </div>
          </div>
          {/* Buttons Row - Multiple rows on mobile, flex-end aligned, full text */}
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 flex-shrink-0 w-full sm:w-auto">
            {/* Statistics Cards */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-2 sm:px-3 py-2">
              <Package className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs sm:text-sm">
                {packagingOrders} Packaging Orders
              </span>
            </div>

            {/* Refresh Button */}
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent country expansion toggle
                // Use onLoadCountryDataWithFilters to preserve all active filters including timePeriodBy and custom date range
                const filters: any = {
                  search: settings.search,
                  sortBy: settings.sortBy,
                  sortOrder: settings.sortOrder,
                  statusFilter: settings.statusFilter,
                  periodFilter: settings.periodFilter,
                  timePeriodBy: settings.timePeriodBy,
                };
                
                // Add custom date range if custom period is selected
                if (settings.periodFilter === "custom") {
                  filters.customStartDate = appliedCustomDateRange.start;
                  filters.customEndDate = appliedCustomDateRange.end;
                }
                
                onLoadCountryDataWithFilters(country, pagination.currentPage, filters);
              }}
              disabled={pagination.loading}
              className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`w-3 h-3 sm:w-4 sm:h-4 ${
                  pagination.loading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table - Collapsible */}
      {isExpanded && (
        <>
          {/* Search Indicator */}
          {settings.search && (
            <div className="px-6 py-2 bg-blue-50 border-l-4 border-blue-400">
              <div className="flex items-center justify-between">
                <p className="text-sm text-blue-800">
                  🔍 Showing results for: "{settings.search}"
                </p>
                <span className="text-xs text-blue-600">
                  {(settings.orders || []).length} result
                  {(settings.orders || []).length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}

          {/* Table Header with Search, Filter, and Sort */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50 w-full max-w-full">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-4 gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-wrap">
                {/* Search by Order ID, Customer Name, or Tracking */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-initial">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by Order ID, Name, or Tracking..."
                      value={settings.search}
                      onChange={(e) => {
                        const newSearchValue = e.target.value;
                        onUpdateCountrySettings(country, {
                          search: newSearchValue,
                        });

                        // Auto-reload when search is cleared
                        if (newSearchValue === "") {
                          onCountrySearch(country, "");
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          onCountrySearch(country, settings.search);
                        }
                      }}
                      className="border border-gray-300 rounded-md pl-10 pr-24 sm:pr-24 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-80"
                    />
                    {settings.search && (
                      <button
                        onClick={() => {
                          onUpdateCountrySettings(country, { search: "" });
                          onCountrySearch(country, "");
                        }}
                        className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    {/* Search Button - Icon only, on right side of input */}
                    <button
                      onClick={() => onCountrySearch(country, settings.search)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-1.5 rounded-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#264a3e] z-10"
                      style={{ backgroundColor: "#264a3e" }}
                      title="Search"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Card/Table View Toggle - Icon only */}
                  <button
                    onClick={() => setLocalViewMode(localViewMode === "table" ? "cards" : "table")}
                    className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors touch-manipulation flex-shrink-0"
                    title={localViewMode === "table" ? "Switch to card view" : "Switch to table view"}
                  >
                    {localViewMode === "table" ? (
                      <LayoutGrid className="w-4 h-4" />
                    ) : (
                      <List className="w-4 h-4" />
                    )}
                  </button>
                </div>
                
                {/* Status Filter */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={settings.statusFilter || "all"}
                    onChange={(e) => {
                      onCountryFilter(country, e.target.value);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  >
                    <option value="all">All Statuses</option>
                    <option value="5">Approved Cancelled</option>
                    <option value="6">Packaging</option>
                    <option value="selected-for-post-office">Post Office Selected</option>
                    <option value="7">Sending</option>
                    <option value="in-transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="returned">Returned</option>
                  </select>
                </div>

                {/* Period Filter */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={settings.periodFilter || "current_month"}
                    onChange={(e) => {
                      const newPeriodFilter = e.target.value;
                      console.log(
                        `🔍 [PERIOD] Changing ${country} period filter from "${settings.periodFilter}" to "${newPeriodFilter}"`
                      );
                      
                      // Show/hide custom date range selector
                      setShowCustomDateRange(newPeriodFilter === "custom");
                      
                      // Update settings first
                      onUpdateCountrySettings(country, {
                        periodFilter: newPeriodFilter,
                      });
                      
                      // Use the new period filter value directly instead of relying on updated state
                      console.log(`🔄 [PERIOD] Auto-triggering refresh for ${country} with new period: ${newPeriodFilter}`);
                      
                      // Build filters object with custom date range if needed
                      const filters: any = {
                        search: settings.search,
                        sortBy: settings.sortBy,
                        sortOrder: settings.sortOrder,
                        statusFilter: settings.statusFilter,
                        periodFilter: newPeriodFilter,
                        timePeriodBy: settings.timePeriodBy,
                      };
                      
                      // Add custom date range if custom period is selected
                      if (newPeriodFilter === "custom") {
                        filters.customStartDate = appliedCustomDateRange.start;
                        filters.customEndDate = appliedCustomDateRange.end;
                      }
                      
                      // Use the correct function with filters
                      onLoadCountryDataWithFilters(country, 1, filters);
                      
                      // Force re-render after a short delay
                      setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                        console.log(`🔄 [PERIOD] Force refresh triggered for ${country}`);
                      }, 200);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  >
                    <option value="today">Today</option>
                    <option value="current_week">This Week</option>
                    <option value="current_month">This Month</option>
                    <option value="current_year">This Year</option>
                    <option value="custom">Custom Range</option>
                    <option value="all">All Time</option>
                  </select>
                </div>

                {/* Time Period By Selector - Available for all time periods */}
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                  <span className="text-sm text-gray-600 whitespace-nowrap">by</span>
                  <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={settings.timePeriodBy || "approveTime"}
                    onChange={(e) => {
                      const newTimePeriodBy = e.target.value;
                      
                      // Update settings first
                      onUpdateCountrySettings(country, {
                        timePeriodBy: newTimePeriodBy,
                      });
                      
                      // Use the new timePeriodBy value directly instead of relying on updated state
                      console.log(`🔄 [TIME PERIOD BY] Auto-triggering refresh for ${country} with new timePeriodBy: ${newTimePeriodBy}`);
                      
                      // Build filters object with custom date range if needed
                      const filters: any = {
                        search: settings.search,
                        sortBy: settings.sortBy,
                        sortOrder: settings.sortOrder,
                        statusFilter: settings.statusFilter,
                        periodFilter: settings.periodFilter,
                        timePeriodBy: newTimePeriodBy,
                      };
                      
                      // Add custom date range if custom period is selected
                      if (settings.periodFilter === "custom") {
                        filters.customStartDate = appliedCustomDateRange.start;
                        filters.customEndDate = appliedCustomDateRange.end;
                      }
                      
                      // Use the correct function with filters
                      onLoadCountryDataWithFilters(country, 1, filters);
                      
                      // Force re-render after a short delay
                      setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                        console.log(`🔄 [TIME PERIOD BY] Force refresh triggered for ${country}`);
                      }, 200);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] w-full sm:w-auto"
                  >
                    <option value="orderDate">Order Date</option>
                    <option value="approveTime">Approve Time</option>
                  </select>
                </div>
              </div>

              {/* <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  {orders.length} orders
                  {settings.search && (
                    <span className="text-blue-600 ml-2">
                      (filtered from {pagination.totalCount})
                    </span>
                  )}
                </div>
                <button
                  onClick={() =>
                    onLoadCountryData(country, pagination.currentPage)
                  }
                  disabled={pagination.loading}
                  className="flex items-center space-x-1 text-white px-3 py-1 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ backgroundColor: "#264a3e" }}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${
                      pagination.loading ? "animate-spin" : ""
                    }`}
                  />
                  <span className="text-sm">Refresh</span>
                </button>
              </div> */}
            </div>
          </div>

          {/* Custom Date Range Selector */}
          {showCustomDateRange && (
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Custom Date Range:
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 flex-1">
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] w-full sm:w-auto"
                  />
                  <span className="text-gray-500 text-center sm:text-left">to</span>
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) =>
                      setCustomDateRange((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-[#264a3e] w-full sm:w-auto"
                  />
                  <button
                    onClick={() => {
                      // Apply the custom date range
                      setAppliedCustomDateRange(customDateRange);
                      
                      // Trigger reload with custom date range
                      onLoadCountryDataWithFilters(country, 1, {
                        search: settings.search,
                        sortBy: settings.sortBy,
                        sortOrder: settings.sortOrder,
                        statusFilter: settings.statusFilter,
                        periodFilter: "custom",
                        timePeriodBy: settings.timePeriodBy,
                        customStartDate: customDateRange.start,
                        customEndDate: customDateRange.end,
                      });
                      
                      // Force re-render
                      setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                      }, 200);
                    }}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors w-full sm:w-auto"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Apply Range
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pending Next Stage Selections Banner */}
          {pendingNextStageSelections.size > 0 && (
            <div className="px-6 py-4 border-b border-orange-200 bg-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-orange-900">
                      {pendingNextStageSelections.size} order
                      {pendingNextStageSelections.size !== 1 ? "s" : ""} selected for next stage
                    </p>
                    <p className="text-xs text-orange-700 mt-1">
                      Current: <span className="font-medium">{currentSelectionStage}</span> → 
                      Next: <span className="font-medium">
                        {(() => {
                          const pendingOrders = getPendingNextStageOrders();
                          if (pendingOrders.length > 0) {
                            const stage = pendingOrders[0].trackingId 
                              ? "shipped" 
                              : pendingOrders[0].selectedPostOffice 
                                ? "with-post-office" 
                                : "without-post-office";
                            return stage;
                          }
                          return "unknown";
                        })()}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={onProgressToNextStage}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Current Stage
                  </button>
                  <button
                    onClick={onClearPendingSelections}
                    className="text-orange-600 hover:text-orange-800"
                    title="Dismiss pending selections"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Status Cards for this Country HIDDEN FOR NOW */}
          {/* <div className="px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"> */}
              {/* Total Shipments */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Truck className="w-6 h-6" style={{ color: "#264a3e" }} />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Total Shipments
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {filteredStats.Transit +
                        filteredStats.Delivered +
                        filteredStats.Returned}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Complicated / Registered */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {complicatedCount > 0 ? (
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    ) : (
                      <Package
                        className="w-6 h-6"
                        style={{ color: "#264a3e" }}
                      />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      {complicatedCount > 0 ? "Complicated" : "Registered"}
                    </p>
                    <p
                      className={`text-lg font-semibold ${
                        complicatedCount > 0 ? "text-orange-600" : ""
                      }`}
                      style={complicatedCount === 0 ? { color: "#264a3e" } : undefined}
                    >
                      {complicatedCount > 0 ? complicatedCount : stats.registered}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* In Transit */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Truck className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      In Transit
                    </p>
                    <p className="text-lg font-semibold text-yellow-600">
                      {filteredStats.Transit}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Delivered */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Delivered
                    </p>
                    <p className="text-lg font-semibold text-green-600">
                      {filteredStats.Delivered}
                    </p>
                  </div>
                </div>
              </div> */}

              {/* Returned */}
              {/* <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Returned
                    </p>
                    <p className="text-lg font-semibold text-red-600">
                      {filteredStats.Returned}
                    </p>
                  </div>
                </div>
              </div> */}
            {/* </div>
          </div> */}
          {/* Bulk Action Bar - Only show if orders from THIS country are selected */}
          {selectedOrdersCompatibility.count > 0 && (
            <div
              className="px-4 sm:px-6 py-4 border-t border-b"
              style={{ backgroundColor: "#f0f9f4", borderColor: "#264a3e" }}
            >
              <div className="flex flex-col space-y-3">
                {/* Header: Selection Info and Clear Button */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <CheckCircle
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      style={{ color: "#264a3e" }}
                    />
                    <span
                      className="text-xs sm:text-sm font-medium cursor-default"
                      style={{ color: "#264a3e" }}
                    >
                      {selectedOrdersCompatibility.count} order
                      {selectedOrdersCompatibility.count !== 1
                        ? "s"
                        : ""}{" "}
                      selected
                    </span>
                    {/* View All Selection Button */}
                    <button
                      onClick={() => setViewAllSelectionModal(true)}
                      className="inline-flex items-center px-2 sm:px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                      title="View all selected orders"
                    >
                      <Eye className="w-3 h-3 sm:mr-1" />
                      <span className="hidden sm:inline">View All</span>
                    </button>
                  </div>

                  {/* Clear Selection */}
                  <button
                    onClick={() => {
                      // Deselect all orders for this country using the proper function
                      onToggleCountryOrdersSelection(country, false);
                    }}
                    className="text-xs sm:text-sm font-medium hover:opacity-80 whitespace-nowrap"
                    style={{ color: "#264a3e" }}
                  >
                    Clear Selection
                  </button>
                </div>

                  {/* Bulk Actions */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap">
                    {selectedOrdersCompatibility.type ===
                      "without-post-office" &&
                      country.toLowerCase() !== "rs" && (
                        <>
                          {!showBulkPostOfficeSelector ? (
                            <button
                              onClick={() =>
                                setShowBulkPostOfficeSelector(true)
                              }
                              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors w-full sm:w-auto"
                            >
                              <MapPin className="w-4 h-4 sm:mr-2 flex-shrink-0" />
                              <span className="hidden sm:inline">Select Post Office for </span>
                              <span className="sm:hidden">Select PO</span>
                              <span className="hidden lg:inline">
                                {" "}{selectedOrdersCompatibility.count} Order
                                {selectedOrdersCompatibility.count !== 1
                                  ? "s"
                                  : ""}
                              </span>
                            </button>
                          ) : (
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                              <select
                                value={bulkPostOffice}
                                onChange={(e) =>
                                  setBulkPostOffice(e.target.value)
                                }
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e] text-xs sm:text-sm w-full sm:w-auto"
                                style={{ minWidth: "200px" }}
                              >
                                <option value="">Select Post Office...</option>
                                <option value="ACS">ACS</option>
                                <option value="Easy Mail">Easy Mail</option>
                              </select>
                              <button
                                onClick={handleBulkPostOfficeSelect}
                                disabled={!bulkPostOffice || isBulkProcessing}
                                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                              >
                                {isBulkProcessing ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 sm:mr-2 animate-spin" />
                                    <span className="hidden sm:inline">Processing...</span>
                                    <span className="sm:hidden">Processing</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 sm:mr-2" />
                                    Apply
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setShowBulkPostOfficeSelector(false);
                                  setBulkPostOffice("");
                                }}
                                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
                              >
                                <X className="w-4 h-4 sm:mr-1" />
                                Cancel
                              </button>
                            </div>
                          )}
                        </>
                      )}

                    {selectedOrdersCompatibility.type === "with-post-office" &&
                      country.toLowerCase() !== "rs" && (
                        <>
                          <button
                            onClick={handleBulkSendToShipping}
                            disabled={isBulkProcessing}
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            {isBulkProcessing ? (
                              <>
                                <svg className="animate-spin h-4 w-4 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="hidden sm:inline">Sending...</span>
                                <span className="sm:hidden">Sending</span>
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 sm:mr-2" />
                                <span className="hidden lg:inline">
                                  Send {selectedOrdersCompatibility.count} Order
                                  {selectedOrdersCompatibility.count !== 1
                                    ? "s"
                                    : ""}{" "}
                                  to Shipping
                                </span>
                                <span className="lg:hidden">Send to Shipping</span>
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={handleBulkClearPostOffice}
                            disabled={isBulkProcessing}
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-red-600 text-xs sm:text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            {isBulkProcessing ? (
                              <>
                                <svg className="animate-spin h-4 w-4 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="hidden sm:inline">Clearing...</span>
                                <span className="sm:hidden">Clearing</span>
                              </>
                            ) : (
                              <>
                                <X className="w-4 h-4 sm:mr-2" />
                                Clear Post Office
                              </>
                            )}
                          </button>
                        </>
                      )}

                    {selectedOrdersCompatibility.type ===
                      "without-post-office" &&
                      country.toLowerCase() === "rs" && (
                        <button 
                          onClick={async () => {
                            const selectedOrdersList = getSelectedOrdersForCountry(country);
                            const orderIds = selectedOrdersList.map(order => order.id);
                            
                            if (orderIds.length === 0) return;
                            
                            const confirmed = window.confirm(
                              `Send ${orderIds.length} order${orderIds.length !== 1 ? 's' : ''} to BEX?\n\nThis will create shipments for selected orders.`
                            );
                            
                            if (!confirmed) return;
                            
                            setIsBulkProcessing(true);
                            
                            try {
                              let successCount = 0;
                              let failCount = 0;
                              
                              // Process each order sequentially
                              for (const orderId of orderIds) {
                                try {
                                  const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ orderId }),
                                  });
                                  
                                  const result = await response.json();
                                  
                                  if (result.success) {
                                    successCount++;
                                    console.log(`✅ [BULK BEX] Order ${orderId} sent successfully`);
                                    // Small delay between requests
                                    await new Promise(resolve => setTimeout(resolve, 300));
                                  } else {
                                    failCount++;
                                    console.error(`❌ [BULK BEX] Order ${orderId} failed:`, result.error);
                                  }
                                } catch (error) {
                                  failCount++;
                                  console.error(`❌ [BULK BEX] Order ${orderId} error:`, error);
                                }
                              }
                              
                              // Reload data for all affected countries
                              const affectedCountries = new Set<string>();
                              selectedOrdersList.forEach(order => {
                                affectedCountries.add(order.country?.toLowerCase() || '');
                              });
                              
                              for (const affectedCountry of Array.from(affectedCountries)) {
                                const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
                                await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
                                  search: settings.search,
                                  sortBy: settings.sortBy,
                                  sortOrder: settings.sortOrder,
                                  statusFilter: settings.statusFilter,
                                  periodFilter: settings.periodFilter,
                                  timePeriodBy: settings.timePeriodBy,
                                });
                              }
                              
                              // Clear selection
                              onToggleCountryOrdersSelection(country, false);
                              
                              if (failCount > 0) {
                                alert(`BEX Bulk Send:\n✅ Success: ${successCount} orders\n❌ Failed: ${failCount} orders`);
                              }
                            } catch (error) {
                              console.error("❌ [BULK BEX] Error:", error);
                              alert("Error sending orders to BEX. Please try again.");
                            } finally {
                              setIsBulkProcessing(false);
                            }
                          }}
                          disabled={isBulkProcessing}
                          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {isBulkProcessing ? (
                            <>
                              <svg className="animate-spin h-4 w-4 sm:mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="hidden sm:inline">Sending...</span>
                              <span className="sm:hidden">Sending</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 sm:mr-2" />
                              <span className="hidden lg:inline">
                                Send {selectedOrdersCompatibility.count} Order
                                {selectedOrdersCompatibility.count !== 1 ? "s" : ""} to Bex
                              </span>
                              <span className="lg:hidden">Send to Bex</span>
                            </>
                          )}
                        </button>
                      )}

                    {selectedOrdersCompatibility.type === "shipped" && (
                      <>
                        {/* Bulk Delete Button */}
                        <button
                          onClick={handleBulkDelete}
                          disabled={isBulkProcessing}
                          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                        >
                          {isBulkProcessing ? (
                            <>
                              <RefreshCw className="w-4 h-4 sm:mr-2 animate-spin" />
                              <span className="hidden sm:inline">Deleting...</span>
                              <span className="sm:hidden">Deleting</span>
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4 sm:mr-2" />
                              <span className="hidden lg:inline">
                                Delete {selectedOrdersCompatibility.count}{" "}
                                Shipment
                                {selectedOrdersCompatibility.count !== 1
                                  ? "s"
                                  : ""}
                              </span>
                              <span className="lg:hidden">Delete Shipment{selectedOrdersCompatibility.count !== 1 ? "s" : ""}</span>
                            </>
                          )}
                        </button>

                        {/* Bulk Preview Button (no limit, hidden if pickup list issued) */}
                        {!selectedOrdersCompatibility.hasPickupList && (
                          <button
                            onClick={handleBulkPreview}
                            disabled={
                              isBulkProcessing ||
                              selectedOrdersCompatibility.count === 0
                            }
                            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                            title={
                              selectedOrdersCompatibility.count === 0
                                ? "Please select at least 1 order"
                                : `Preview ${selectedOrdersCompatibility.count} label${selectedOrdersCompatibility.count !== 1 ? 's' : ''} (in batches of 9)`
                            }
                          >
                            <Eye className="w-4 h-4 sm:mr-2" />
                            <span className="hidden lg:inline">
                              Preview {selectedOrdersCompatibility.count} Label
                              {selectedOrdersCompatibility.count !== 1 ? "s" : ""}
                            </span>
                            <span className="lg:hidden">Preview Labels</span>
                          </button>
                        )}

                        {/* Show info message if pickup list is issued */}
                        {selectedOrdersCompatibility.hasPickupList && (
                          <div className="text-xs sm:text-sm text-gray-600">
                            <AlertCircle className="w-4 h-4 inline mr-1 text-blue-600" />
                            <span className="hidden sm:inline">
                              Selected orders have pickup lists issued - preview not available
                            </span>
                            <span className="sm:hidden">Pickup list issued - preview unavailable</span>
                          </div>
                        )}
                      </>
                    )}

                    {selectedOrdersCompatibility.type === "mixed" && (
                      isBulkProcessing ? (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <svg className="animate-spin h-4 w-4 inline mr-1 text-[#264a3e]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="hidden sm:inline">Processing bulk operation...</span>
                          <span className="sm:hidden">Processing...</span>
                        </div>
                      ) : (
                        <div className="text-xs sm:text-sm text-gray-600">
                          <AlertTriangle className="w-4 h-4 inline mr-1 text-yellow-600" />
                          <span className="hidden sm:inline">
                            Selected orders are in different states. Please select
                            orders in the same state for bulk actions.
                          </span>
                          <span className="sm:hidden">Orders in different states</span>
                        </div>
                      )
                    )}
                  </div>
              </div>
            </div>
          )}


          {/* Orders Table/Cards */}
          {orders.length > 0 && (localViewMode === "cards" ? (
            /* Card View */
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {orders.map((order) => {
                  const isSelected = selectedOrders.has(order.id);
                  const isSending = sendingToShipping.has(order.id);
                  
                  // Get account information (same logic as table view)
                  let account: { id: number | null; username: string | null; email: string | null; level: string; team: string | null } | undefined;
                  
                  if (order.accountInfo && order.accountInfo.id !== null && order.accountInfo.username !== null) {
                    account = order.accountInfo;
                  } else if (order.user) {
                    // AlterCPA removed for Greek-CC
                    account = undefined; // getAccountById(altercpaState, order.user);
                  }
                  
                  // Get best timestamp for approved time
                  const approvedTimestamp = getBestTimestamp(order);
                  const approvedDate = approvedTimestamp ? new Date(approvedTimestamp) : null;
                  
                  // Get order date
                  const orderDate = parseDate(order.time);
                  
                  // Calculate total price
                  const pricing = getOrderPricing(order);
                  
                  // Get status badges
                  const altercpaStatus = getAlterCPAStatus(order);
                  const postOfficeStatus = getPostOfficeStatus(order);
                  
                  return (
                    <div 
                      key={order.id} 
                      className={`bg-white border rounded-lg p-4 hover:shadow-lg transition-all ${
                        isSelected ? "border-[#264a3e] border-2 bg-green-50" : "border-gray-200"
                      }`}
                    >
                      {/* Header: Order ID and Checkbox */}
                      <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-gray-900">#{order.id}</span>
                          {order.trackingId && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Shipped
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleOrderSelection(order.id, order)}
                          className="h-5 w-5 border-gray-300 rounded flex-shrink-0 cursor-pointer"
                          style={{ accentColor: "#264a3e" }}
                        />
                      </div>
                      
                      {/* Customer Information */}
                      <div className="mb-3 space-y-2">
                        <div className="flex items-start space-x-2">
                          <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{order.name || "N/A"}</div>
                            {order.phone && (
                              <div className="text-xs text-gray-600 mt-0.5">
                                <Phone className="w-3 h-3 inline mr-1" />
                                {formatPhoneNumber(order.phone, country.toLowerCase() === "rs")}
                              </div>
                            )}
                            {order.email && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate">
                                <Mail className="w-3 h-3 inline mr-1" />
                                {order.email}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Address */}
                        {(order.area || order.city || order.street || order.addr) && (
                          <div className="flex items-start space-x-2 pt-1">
                            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="text-xs text-gray-600 flex-1">
                              {order.area && <span>{order.area}, </span>}
                              {order.city && <span>{order.city}</span>}
                              {(order.index || order.zipcode) && order.city && <span>, {order.index || order.zipcode}</span>}
                              {(order.street || order.addr) && (
                                <div className="mt-0.5">{order.street || order.addr}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Product Information */}
                      {order.offername && (
                        <div className="mb-3 pb-3 border-b border-gray-200">
                          <div className="text-sm font-medium text-gray-900 mb-1">{order.offername}</div>
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <span>Qty: {pricing.quantity}</span>
                            <span className="font-semibold text-gray-900">
                              {formatPriceValue(pricing.total, pricing.currency)}
                            </span>
                          </div>
                          {pricing.needsExtraShipping && (
                            <div className="mt-1 text-xs font-medium text-orange-700">
                              +{formatPriceValue(pricing.extraShippingFee, pricing.currency)} Shipping Cost
                            </div>
                          )}
                          {order.comment && (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              Note: {order.comment}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Account Information */}
                      <div className="mb-3 pb-3 border-b border-gray-200 space-y-1.5">
                        {(() => {
                          // For manual orders
                          if (order.source === "manual" && order.createdBy) {
                            return (
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <div className="text-xs">
                                  <div className="font-medium text-gray-900">{order.createdBy.username || "N/A"}</div>
                                  <div className="text-gray-500">{order.createdBy.role || "Manual"}</div>
                                </div>
                              </div>
                            );
                          }
                          
                          // For AlterCPA orders
                          if (order.user && account) {
                            return (
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <div className="text-xs">
                                  <div className="font-medium text-gray-900">
                                    #{order.user} - {account.username || "N/A"}
                                  </div>
                                  {account.level && (
                                    <div className="text-gray-500">Level: {account.level}</div>
                                  )}
                                  {account.team && (
                                    <div className="text-gray-500">Team: {account.team}</div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                      
                      {/* Dates and Status */}
                      <div className="mb-3 space-y-2">
                        {orderDate && (
                          <div className="flex items-center space-x-2 text-xs">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <span className="text-gray-500">Order Date: </span>
                              <span className="text-gray-900 font-medium">
                                {orderDate.toLocaleDateString()} {orderDate.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {approvedDate && (
                          <div className="flex items-center space-x-2 text-xs">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <span className="text-gray-500">Approved: </span>
                              <span className="text-gray-900 font-medium">
                                {approvedDate.toLocaleDateString()} {approvedDate.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Status Badges */}
                        <div className="flex items-center space-x-2 pt-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${altercpaStatus.color}`}
                          >
                            {altercpaStatus.name}
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${postOfficeStatus.color}`}
                          >
                            {postOfficeStatus.name}
                          </span>
                        </div>
                        
                        {/* Tracking ID */}
                        {order.trackingId && (
                          <div className="flex items-center space-x-2 text-xs pt-1">
                            <Package className="w-4 h-4 text-blue-600" />
                            <div className="flex-1">
                              <span className="text-gray-500">Tracking: </span>
                              <span className="text-blue-600 font-medium">{order.trackingId}</span>
                              {order.shippingCompany && (
                                <span className="text-gray-500 ml-1">({order.shippingCompany.toUpperCase()})</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons for card view */}
                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                        {country.toLowerCase() === "rs" && isConnected && !order.trackingId && (
                          <button
                            onClick={async () => {
                              try {
                                if (!order.area || !order.city || !order.addr) {
                                  alert("Please edit the order first to complete the BEX address.");
                                  return;
                                }
                                setIsBulkProcessing(true);
                                const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ orderId: order.id }),
                                });
                                const result = await response.json();
                                if (result.success) {
                                  await onLoadCountryDataWithFilters(country, pagination.currentPage, {
                                    search: settings.search,
                                    sortBy: settings.sortBy,
                                    sortOrder: settings.sortOrder,
                                    statusFilter: settings.statusFilter,
                                    periodFilter: settings.periodFilter,
                                    timePeriodBy: settings.timePeriodBy,
                                  });
                                } else {
                                  alert(`Failed to send to BEX: ${result.error || "Unknown error"}`);
                                }
                              } catch (error) {
                                console.error("Error sending to BEX:", error);
                                alert("Failed to send to BEX. Please try again.");
                              } finally {
                                setIsBulkProcessing(false);
                              }
                            }}
                            disabled={isSending || isBulkProcessing}
                            className="flex items-center space-x-1 text-white px-3 py-1.5 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            style={{ backgroundColor: "#264a3e" }}
                          >
                            {isSending || isBulkProcessing ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Send className="w-3 h-3" />
                            )}
                            <span>Send to Bex</span>
                          </button>
                        )}
                        {country.toLowerCase() !== "rs" && (
                          <PostOfficeSelector
                            order={order}
                            onSelectPostOffice={onSelectPostOffice}
                            onClearPostOffice={onClearPostOffice}
                            onSendToShipping={handleSendToShipping}
                            onPrintLabel={onPrintLabel}
                            onViewLabel={onViewLabel}
                            onDeleteShipment={onDeleteShipment}
                            isLoading={isSending}
                          />
                        )}
                        {!order.trackingId && !order.selectedPostOffice && (
                          <button
                            onClick={() => setEditModal({ isOpen: true, order })}
                            className="flex items-center space-x-1 text-gray-600 hover:text-[#264a3e] px-2 py-1.5 rounded text-xs transition-colors"
                            title="Edit order"
                          >
                            <Edit className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Table View */
            <div
              className="overflow-x-auto border border-gray-200 rounded-lg w-full relative overflow-hidden"
              style={{ minHeight: "500px" }}
            >
            {/* Loading overlay */}
            {pagination.loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 animate-spin text-[#264a3e]" />
                  <span className="text-sm text-gray-600">Loading orders...</span>
                </div>
              </div>
            )}
            <table
              className="min-w-full divide-y divide-gray-200"
              style={{ minWidth: "1400px" }}
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={
                        orders.length > 0 &&
                        orders.every((order) => selectedOrders.has(order.id))
                      }
                      onChange={(e) => {
                        onToggleCountryOrdersSelection(
                          country,
                          e.target.checked
                        );
                      }}
                      className="h-4 w-4 border-gray-300 rounded"
                      style={{
                        accentColor: "#264a3e",
                        color: "#264a3e",
                      }}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span style={{ minWidth: "65px" }}>ORDER ID</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>CUSTOMER</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">
                    Comment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACCOUNT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {settings.timePeriodBy === "orderDate" ? (
                      <button
                        onClick={() => {
                          const currentSort =
                            settings.sortBy === "time"
                              ? settings.sortOrder
                              : "none";
                          let newSortOrder: "asc" | "desc" | "none";

                          if (currentSort === "none") {
                            newSortOrder = "desc";
                          } else if (currentSort === "desc") {
                            newSortOrder = "asc";
                          } else {
                            newSortOrder = "none";
                          }

                          onCountrySort(country, "time", newSortOrder);
                        }}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>DATE</span>
                        {settings.sortBy === "time" &&
                          settings.sortOrder !== "none" &&
                          (settings.sortOrder === "desc" ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronUp className="w-4 h-4" />
                          ))}
                      </button>
                    ) : (
                      <span>DATE</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {settings.timePeriodBy === "approveTime" ? (
                      <button
                        onClick={() => {
                          const currentSort =
                            settings.sortBy === "packingTimestamp"
                              ? settings.sortOrder
                              : "none";
                          let newSortOrder: "asc" | "desc" | "none";

                          if (currentSort === "none") {
                            newSortOrder = "desc";
                          } else if (currentSort === "desc") {
                            newSortOrder = "asc";
                          } else {
                            newSortOrder = "none";
                          }

                          onCountrySort(country, "packingTimestamp", newSortOrder);
                        }}
                        className="flex items-center space-x-1 hover:text-gray-700"
                      >
                        <span>APPROVE TIME</span>
                        {settings.sortBy === "packingTimestamp" &&
                          settings.sortOrder !== "none" &&
                          (settings.sortOrder === "desc" ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronUp className="w-4 h-4" />
                          ))}
                      </button>
                    ) : (
                      <span>APPROVE TIME</span>
                    )}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRACKING
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ALTERCPA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>POST OFFICE</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(settings.orders || []).map((order) => {
                  // Use accountInfo from order (backend enrichment) if available, otherwise lookup from Redux
                  // accountInfo has structure: { id, username, email, level, team }
                  let account: { id: number | null; username: string | null; email: string | null; level: string; team: string | null } | undefined;
                  
                  if (order.accountInfo && order.accountInfo.id !== null && order.accountInfo.username !== null) {
                    // Use backend-enriched accountInfo if it has valid data
                    account = order.accountInfo;
                  } else if (order.user) {
                    // Fallback removed - AlterCPA not available in Greek-CC
                    account = undefined; // getAccountById(altercpaState, order.user);
                  }
                  const isSelected = selectedOrders.has(order.id);
                  const isSending = sendingToShipping.has(order.id);
                  const pricing = getOrderPricing(order);

                  // Check if this order can be selected (stage compatibility for individual selection)
                  let isSelectable = true;
                  let disableReason = "";

                  if (selectedOrders.size > 0 && !isSelected) {
                    const orderStage = getOrderStage(order);
                    
                    if (currentSelectionStage !== null && currentSelectionStage !== orderStage) {
                      isSelectable = false;
                      disableReason = "Orders must be in the same stage. Open stage selection modal to choose which stage to keep.";
                    }
                  }

                  return (
                    <tr
                      key={order.id}
                      className={`${!isConnected ? "bg-red-25" : ""} ${
                        isSelected
                          ? "border-l-4"
                          : !isSelectable
                          ? "bg-gray-50 opacity-60"
                          : "hover:bg-gray-50"
                      }`}
                      style={
                        isSelected
                          ? {
                              backgroundColor: "#f0f9f4",
                              borderLeftColor: "#264a3e",
                            }
                          : {}
                      }
                      title={!isSelectable ? disableReason : ""}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleOrderSelection(order.id, order)}
                          disabled={!isSelectable}
                          className="h-4 w-4 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            accentColor: "#264a3e",
                            color: "#264a3e",
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{order.id}
                      </td>
                      <td className="px-6 py-4" style={{ maxWidth: "250px" }}>
                        <div className="text-sm text-gray-900 break-words">
                          <div className="text-sm font-medium text-gray-900 break-words mb-2">
                            {order.name || "N/A"}
                          </div>
                          <div className="flex items-center mb-1">
                            <Phone className="w-3 h-3 text-gray-400 mr-1" />
                            <span className="font-mono text-sm">
                              {formatPhoneNumber(order.phone, country.toLowerCase() === "rs")}
                            </span>
                          </div>
                          {order.email && (
                            <div className="flex items-center">
                              <Mail className="w-3 h-3 text-gray-400 mr-1" />
                              <span className="text-xs text-gray-500 break-words">
                                {order.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4" style={{ maxWidth: "250px" }}>
                        <div className="text-sm text-gray-900 break-words">
                          {/* Area/Region with location pin */}
                          {order.area && (
                            <div className="flex items-center mb-1">
                              <MapPin className="w-3 h-3 text-gray-400 mr-1" />
                              <span className="text-sm text-gray-900">
                                {order.area}
                              </span>
                            </div>
                          )}

                          {/* City and Zip Code */}
                          {(order.city || order.index || order.zipcode) && (
                            <div className="text-xs text-gray-600 mb-1">
                              {order.city}
                              {(order.index || order.zipcode) &&
                                order.city &&
                                ", "}
                              {order.index || order.zipcode}
                            </div>
                          )}

                          {/* Street Address */}
                          {(order.street || order.addr) && (
                            <div className="text-xs text-gray-500 break-words">
                              {[order.street, order.addr]
                                .filter(Boolean)
                                .join(" ")}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 min-w-[180px]">
                        <div className="text-sm font-medium text-gray-900 break-words">
                          {order.offername || "N/A"}
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs text-gray-500">
                            Qty: {pricing.quantity} • {formatPriceValue(pricing.unitPrice, pricing.currency)} each
                          </div>
                          {pricing.needsExtraShipping && (
                            <div className="text-xs font-semibold text-orange-700">
                              +{formatPriceValue(pricing.extraShippingFee, pricing.currency)} Shipping
                            </div>
                          )}
                          <div className="text-xs font-semibold text-gray-800">
                            Total: {formatPriceValue(pricing.total, pricing.currency)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 w-[200px]">
                        <div className="text-xs text-gray-500 break-words">
                          {order.comment || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          // For manual orders, show creator info
                          if (order.source === "manual" && order.createdBy) {
                            return (
                              <div className="flex items-center">
                                <User className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {order.createdBy.username || "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {order.createdBy.role || "N/A"}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // For AlterCPA orders, show user ID and account
                          if (order.user) {
                            return (
                              <div className="flex items-center">
                                <User className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    #{order.user}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {account?.username || "N/A"}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Fallback for orders without user or createdBy
                          return (
                            <div className="flex items-center">
                              <User className="w-4 h-4 text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  N/A
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-500">
                            {(() => {
                              // Use parseDate to handle the timestamp properly
                              const date = parseDate(order.time);
                              return date ? (
                                <>
                                  <div>{date.toLocaleDateString()}</div>
                                  <div className="text-xs text-gray-400">{date.toLocaleTimeString()}</div>
                                </>
                              ) : (
                                <span className="text-gray-400 italic">Invalid date</span>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-500">
                            {getBestTimestamp(order) ? (
                              <div>
                                <div>{new Date(getBestTimestamp(order)!).toLocaleDateString()}</div>
                                <div className="text-xs text-gray-400">{new Date(getBestTimestamp(order)!).toLocaleTimeString()}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Not approved</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.trackingId ? (
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {order.trackingId}
                              </div>
                              <div className="text-xs text-gray-500">
                                {order.shippingCompany?.toUpperCase()}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-orange-500 mr-2" />
                            <span className="text-sm text-gray-500">
                              Pending
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const altercpaStatus = getAlterCPAStatus(order);
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${altercpaStatus.color}`}
                            >
                              {altercpaStatus.name}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const postOfficeStatus = getPostOfficeStatus(order);
                          return (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${postOfficeStatus.color}`}
                            >
                              {postOfficeStatus.name}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {selectedOrders.size === 0 ? (
                          // Normal mode - show all action buttons
                          <div className="flex items-center space-x-2">
                            {/* Post Office Selector / Shipping Actions */}
                            {country.toLowerCase() === "rs" && isConnected && (
                              <>
                                {!order.trackingId ? (
                                  // Show "Send to Bex" button if not yet shipped (no pickup date needed for BEX)
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Validate address before sending
                                        if (!order.area || !order.city || !order.addr) {
                                          alert("Please edit the order first to complete the BEX address (Municipality, City, and Address Number are required).");
                                          return;
                                        }

                                        setIsBulkProcessing(true);
                                        // Set isSending state
                                        // BEX doesn't need pickup date, just send directly
                                        const response = await fetch(`${API_BASE_URL}/api/shipping/bex/send`, {
                                          method: "POST",
                                          headers: { "Content-Type": "application/json" },
                                          body: JSON.stringify({ orderId: order.id }),
                                        });
                                        const result = await response.json();
                                        if (result.success) {
                                          // Reload data
                                          await onLoadCountryDataWithFilters(country, pagination.currentPage, {
                                            search: settings.search,
                                            sortBy: settings.sortBy,
                                            sortOrder: settings.sortOrder,
                                            statusFilter: settings.statusFilter,
                                            periodFilter: settings.periodFilter,
                                            timePeriodBy: settings.timePeriodBy,
                                          });
                                        } else {
                                          alert(`Failed to send to BEX: ${result.error || "Unknown error"}`);
                                        }
                                      } catch (error) {
                                        console.error("Error sending to BEX:", error);
                                        alert("Failed to send to BEX. Please try again.");
                                      } finally {
                                        setIsBulkProcessing(false);
                                      }
                                    }}
                                    disabled={isSending || isBulkProcessing}
                                    className="flex items-center space-x-1 text-white px-3 py-1 rounded text-xs hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    style={{ backgroundColor: "#264a3e" }}
                                  >
                                    {isSending || isBulkProcessing ? (
                                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                      <Send className="w-3 h-3" />
                                    )}
                                    <span>
                                      {isSending || isBulkProcessing ? "Sending..." : "Send to Bex"}
                                    </span>
                                  </button>
                                ) : (
                                  // Show action buttons if already shipped
                                  <div className="flex items-center space-x-2">
                                    {/* Print Label Button */}
                                    <button
                                      onClick={() =>
                                        onPrintLabel(order.id, "a6")
                                      }
                                      className="flex items-center space-x-1 text-white px-2 py-1 rounded text-xs hover:opacity-90 transition-colors"
                                      style={{ backgroundColor: "#264a3e" }}
                                      title="Print A6 Label"
                                    >
                                      <Package className="w-3 h-3" />
                                      <span>A6</span>
                                    </button>

                                    {/* View Tracking Button */}
                                    <button
                                      onClick={() =>
                                        onViewLabel(order.id, "a6")
                                      }
                                      className="flex items-center space-x-1 bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                                      title="View Tracking"
                                    >
                                      <Eye className="w-3 h-3" />
                                      <span>Track</span>
                                    </button>

                                    {/* Delete Shipment Button */}
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Delete shipment for order #${order.id}?\n\nThis will cancel the shipment with BEX and revert the order to its previous status.`
                                          )
                                        ) {
                                          onDeleteShipment(order.id);
                                        }
                                      }}
                                      className="flex items-center space-x-1 bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 transition-colors"
                                      title="Delete Shipment"
                                    >
                                      <X className="w-3 h-3" />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Post Office Selector for Greece and other countries */}
                            {country.toLowerCase() !== "rs" && (
                              <PostOfficeSelector
                                order={order}
                                onSelectPostOffice={onSelectPostOffice}
                                onClearPostOffice={onClearPostOffice}
                                onSendToShipping={handleSendToShipping}
                                onPrintLabel={onPrintLabel}
                                onViewLabel={onViewLabel}
                                onDeleteShipment={onDeleteShipment}
                                isLoading={isSending}
                              />
                            )}

                            {/* Edit button - only visible before post office selection */}
                            {!order.trackingId && !order.selectedPostOffice && (
                              <button
                                onClick={() =>
                                  setEditModal({ isOpen: true, order })
                                }
                                className="flex items-center space-x-1 text-gray-600 hover:text-[#264a3e] px-2 py-1 rounded text-xs transition-colors"
                                title="Edit order details"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ) : isSelected ? (
                          // Bulk selection mode - selected row
                          <div className="flex items-center justify-center">
                            <CheckCircle
                              className="w-5 h-5"
                              style={{ color: "#264a3e" }}
                            />
                            <span
                              className="text-xs font-medium ml-2"
                              style={{ color: "#264a3e" }}
                            >
                              Selected
                            </span>
                          </div>
                        ) : (
                          // Bulk selection mode - not selected row (disabled)
                          <div className="flex items-center justify-center">
                            <span className="text-xs text-gray-400 italic">
                              Actions disabled
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          ))}

          {/* Country Footer */}
          {(settings.orders || []).length === 0 && !pagination.loading && (
            <div className="px-6 py-8 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                No packaging for {country.toUpperCase()} of the selected period
              </p>
            </div>
          )}

          {/* Country Pagination Controls - Always show when there are results or loading */}
          {(pagination.totalPages > 0 || pagination.loading) && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 w-full">
              {pagination.loading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-[#264a3e] mr-2" />
                  <span className="text-sm text-gray-600">Loading...</span>
                </div>
              ) : (
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalCount}
                  itemsPerPage={50}
                  onPageChange={(page) => onCountryPageChange(country, page)}
                  showFirstLast={true}
                  maxVisiblePages={5}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* Edit Order Modal - BEX removed for Greek-CC (Greece uses ACS only) */}
      <EditOrderModal
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, order: null })}
        order={editModal.order}
        onSave={onEditOrder}
      />

      {/* Pickup Date Modal */}
      <PickupDateModal
        isOpen={pickupDateModal.isOpen}
        onClose={() =>
          setPickupDateModal({
            isOpen: false,
            orderId: null,
            orderIds: null,
            shippingCompany: "",
            isBulk: false,
          })
        }
        orderId={pickupDateModal.orderId || 0}
        shippingCompany={pickupDateModal.shippingCompany}
        isBulk={pickupDateModal.isBulk}
        bulkCount={pickupDateModal.orderIds?.length || 0}
        orders={pickupDateModal.orders || []}
        onConfirm={async (pickupDate, notes, priceRounding) => {
          if (pickupDateModal.isBulk && pickupDateModal.orderIds) {
            // Bulk shipping
            setIsBulkProcessing(true);
            
            // Start tracking bulk operation to suppress conflict modal
            onStartBulkOperation?.(country);
            
            // Note: We can't update metadata yet because we haven't called the API
            // The tracking IDs will be set after the API calls complete
            // For now, just update the stage so the UI shows the correct state
            onUpdateStage("shipped");
            console.log('🔄 [BULK] Stage updated to "shipped" immediately');
            
            try {
              console.log(
                `🚚 [BULK] Sending ${pickupDateModal.orderIds.length} orders to ${pickupDateModal.shippingCompany}`
              );
              if (priceRounding && Object.keys(priceRounding).length > 0) {
                console.log(`💰 [PRICE-ROUNDING] Rounding enabled for orders:`, priceRounding);
              }

              // Process each order sequentially
              const errors: Array<{ orderId: number; error: string }> = [];
              const successfulOrders: Array<{ orderId: number; trackingId: string }> = [];
              let successCount = 0;
              
              for (const orderId of pickupDateModal.orderIds) {
                try {
                  const shouldRoundPrice = priceRounding && priceRounding[orderId];
                  // For now, just log the rounding preference
                  // TODO: Implement price rounding in onSendToShipping or the API call
                  if (shouldRoundPrice) {
                    console.log(`💰 [PRICE-ROUNDING] Order ${orderId} will have price rounded`);
                  }
                  
                  // Call the API directly to get the tracking ID from the response
                  const response = await fetch(
                    `${API_BASE_URL}/api/shipping/${pickupDateModal.shippingCompany}/send`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({
                        orderId,
                        pickupDate,
                        pickupDateNotes: notes,
                      }),
                    }
                  );
                  
                  const result = await response.json();
                  
                  if (result.success) {
                    const trackingNumber = result.data?.trackingNumber || result.data?.voucherNumber || result.trackingNumber;
                    
                    if (trackingNumber) {
                      successfulOrders.push({ orderId, trackingId: trackingNumber });
                      console.log(`✅ [BULK] Order ${orderId} sent successfully with tracking: ${trackingNumber}`);
                    } else {
                      console.log(`✅ [BULK] Order ${orderId} sent successfully (no tracking number)`);
                    }
                    
                    successCount++;
                    // Small delay between requests to avoid overwhelming the server
                    await new Promise((resolve) => setTimeout(resolve, 500));
                  } else {
                    throw new Error(result.details || result.error || "Unknown error");
                  }
                } catch (error: any) {
                  console.error(
                    `❌ [BULK] Failed to send order ${orderId}:`,
                    error
                  );
                  
                  // Extract detailed error message
                  let errorMessage = error?.message || error?.toString() || "Unknown error";
                  errors.push({ orderId, error: errorMessage });
                }
              }
              
              // Update metadata with tracking IDs for successful orders
              if (successfulOrders.length > 0) {
                const updatedMeta = { ...selectedOrdersMeta };
                successfulOrders.forEach(({ orderId, trackingId }) => {
                  if (updatedMeta[orderId]) {
                    updatedMeta[orderId] = {
                      ...updatedMeta[orderId],
                      trackingId: trackingId,
                      shippingCompany: pickupDateModal.shippingCompany,
                    };
                  }
                });
                onRefreshSelectedOrdersMeta(country, Object.values(updatedMeta));
                console.log(`✅ [BULK] Updated metadata with tracking IDs for ${successfulOrders.length} orders`);
              }
              
              // Show error alert if any orders failed
              if (errors.length > 0) {
                const errorDetails = errors.map(e => `Order #${e.orderId}: ${e.error}`).join('\n');
                alert(`Shipping Results:\n✅ Successfully sent: ${successCount} orders\n❌ Failed: ${errors.length} orders\n\nErrors:\n${errorDetails}`);
              }

              console.log(
                `✅ [BULK] Completed processing ${pickupDateModal.orderIds.length} orders`
              );

              // Get all affected countries from selected orders
              const affectedCountries = new Set<string>();
              pickupDateModal.orderIds.forEach(orderId => {
                const order = selectedOrdersMeta[orderId];
                if (order && order.country) {
                  affectedCountries.add(order.country.toLowerCase());
                }
              });

              // Reload data for all affected countries but keep current page
              console.log(`🔄 [BULK] Reloading data for ${affectedCountries.size} countries on page ${pagination.currentPage}`);
              for (const affectedCountry of Array.from(affectedCountries)) {
                // Check if this is the current country, preserve page number
                const targetPage = affectedCountry === country.toLowerCase() ? pagination.currentPage : 1;
                await onLoadCountryDataWithFilters(affectedCountry, targetPage, {
                  search: settings.search,
                  sortBy: settings.sortBy,
                  sortOrder: settings.sortOrder,
                  statusFilter: settings.statusFilter,
                  periodFilter: settings.periodFilter,
                  timePeriodBy: settings.timePeriodBy,
                });
              }

              // DON'T Clear selection after bulk operation - keep them selected
              // Orders are now "shipped" stage
              // Metadata will be auto-refreshed by useEffect when settings.orders updates
              
              console.log('✅ [BULK] Shipping completed, stage is already "shipped".');
            } catch (error) {
              console.error("❌ [BULK] Bulk shipping failed:", error);
              throw error; // Re-throw to show error in modal
            } finally {
              // Stop tracking bulk operation
              onStopBulkOperation?.(country);
              setIsBulkProcessing(false);
            }
          } else if (pickupDateModal.orderId) {
            // Single order shipping
            if (pickupDateModal.shippingCompany === "bex") {
              onSendToBex(pickupDateModal.orderId, pickupDate, notes);
            } else {
              onSendToShipping(
                pickupDateModal.orderId,
                pickupDateModal.shippingCompany,
                pickupDate,
                notes
              );
            }
          }
          setPickupDateModal({
            isOpen: false,
            orderId: null,
            orderIds: null,
            shippingCompany: "",
            isBulk: false,
            orders: undefined,
          });
        }}
      />

      {/* View All Selection Modal */}
      <ViewAllSelectionModal
        isOpen={viewAllSelectionModal}
        onClose={() => setViewAllSelectionModal(false)}
        selectedOrders={getSelectedOrdersForCountry(country)}
        onClearSelection={() => onToggleCountryOrdersSelection(country, false)}
      />
    </div>
  );
};

export default CountrySection;
